import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SIDES_TR = { north: "Kuzey", south: "Güney", east: "Doğu", west: "Batı" };

// Spinner keyframe — bir kez DOM'a ekle
const SPINNER_STYLE = `
  @keyframes magic-spin {
    to { transform: rotate(360deg); }
  }
`;

export default function MagicAccept() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const token          = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading | success | error | no_token
  const [result, setResult] = useState(null);
  const [error,  setError]  = useState("");
  // React 18 StrictMode'da useEffect çift çalışmasına karşı koruma
  const calledRef = useRef(false);

  useEffect(() => {
    // Spinner CSS enjekte et
    if (!document.getElementById("magic-accept-styles")) {
      const style = document.createElement("style");
      style.id = "magic-accept-styles";
      style.textContent = SPINNER_STYLE;
      document.head.appendChild(style);
    }

    // Çift çalışmayı engelle (React 18 StrictMode)
    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setStatus("no_token");
      return;
    }
    axios
      .get(`${API}/customer/stand-change-requests/magic-accept`, { params: { token } })
      .then((res) => {
        setResult(res.data);
        setStatus("success");
      })
      .catch((err) => {
        const msg =
          err.response?.data?.detail ||
          "Bir hata oluştu. Token geçersiz ya da süresi dolmuş olabilir.";
        setError(msg);
        setStatus("error");
      });
  }, [token]);

  const goToDashboard = () => {
    const slug = result?.tenant_slug;
    if (slug) {
      navigate(`/musteri-admin/${slug}/layout`);
    } else {
      navigate("/giris");
    }
  };

  /* ── Loading ─────────────────────────────────────────── */
  if (status === "loading") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Stand değişikliği işleniyor…</p>
        </div>
      </div>
    );
  }

  /* ── No token ────────────────────────────────────────── */
  if (status === "no_token") {
    let fallbackSlug = null;
    try {
      const cached = JSON.parse(localStorage.getItem("cop31_user") || "null");
      if (cached?.tenant_slug) fallbackSlug = cached.tenant_slug;
    } catch { /* ignore */ }

    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconCircle("#fef2f2", "#dc2626")}>✕</div>
          <h2 style={{ ...styles.title, color: "#dc2626" }}>Geçersiz Bağlantı</h2>
          <p style={styles.subtitle}>Bu bağlantı eksik veya hatalı görünüyor.</p>
          {fallbackSlug ? (
            <button
              style={styles.btnPrimary}
              data-testid="magic-accept-fallback-layout"
              onClick={() => navigate(`/musteri-admin/${fallbackSlug}/layout`)}
            >
              Stand Planıma Git →
            </button>
          ) : null}
          <button style={styles.btnSecondary} onClick={() => navigate("/giris")}>
            Giriş Sayfasına Git
          </button>
        </div>
      </div>
    );
  }

  /* ── Error ───────────────────────────────────────────── */
  if (status === "error") {
    // Phase A: graceful fallback — if the operator already has a valid session
    // in localStorage (e.g. they were already logged in when they clicked the
    // email link), bounce them straight to their dashboard layout. The "Invalid
    // Link" screen is only useful when there's nothing better to fall back to.
    let fallbackSlug = null;
    try {
      const cached = JSON.parse(localStorage.getItem("cop31_user") || "null");
      if (cached?.tenant_slug) fallbackSlug = cached.tenant_slug;
    } catch { /* ignore */ }

    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.iconCircle("#fef2f2", "#dc2626")}>✕</div>
          <h2 style={{ ...styles.title, color: "#dc2626" }}>İşlem Başarısız</h2>
          <p style={styles.subtitle}>{error}</p>
          <div style={styles.infoBox("#fef2f2", "#fecaca")}>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
              Token süresi 7 gün ile sınırlıdır. Daha uzun süre geçtiyse ya da
              talep zaten yanıtlanmışsa bu hata görünür.
            </p>
          </div>
          {fallbackSlug ? (
            <button
              style={styles.btnPrimary}
              data-testid="magic-accept-fallback-layout"
              onClick={() => navigate(`/musteri-admin/${fallbackSlug}/layout`)}
            >
              Stand Planıma Git →
            </button>
          ) : null}
          <button style={styles.btnSecondary} onClick={() => navigate("/giris")}>
            Giriş Sayfasına Git
          </button>
        </div>
      </div>
    );
  }

  /* ── Success ─────────────────────────────────────────── */
  const openSidesText =
    (result?.open_sides || [])
      .map((s) => SIDES_TR[s] || s)
      .join(", ") || "—";

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.iconCircle("#f0fdf4", "#16a34a")}>✓</div>
        <h1 style={{ ...styles.title, color: "#15803d" }}>Stand Başarıyla Güncellendi!</h1>
        <p style={styles.subtitle}>
          Stand değişiklik talebiniz onaylandı. Yeni stand bilgileriniz aşağıdaki gibidir.
        </p>

        {/* Stand bilgileri */}
        <div style={styles.infoBox("#eff6ff", "#bfdbfe")}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <Row label="Yeni Stand No">
                <span style={{ fontSize: 22, fontWeight: 800, color: "#1d4ed8" }}>
                  {result?.new_stand_no || "—"}
                </span>
              </Row>
              <Row label="Salon / Hall">{result?.new_hall || "—"}</Row>
              <Row label="Alan">{result?.new_area ? `${result.new_area} m²` : "—"}</Row>
              <Row label="Açık Kenarlar">{openSidesText}</Row>
            </tbody>
          </table>
        </div>

        {/* CTA */}
        <button
          style={styles.btnPrimary}
          onClick={goToDashboard}
          data-testid="magic-accept-dashboard-btn"
        >
          Panelinize Gidin →
        </button>
        <p style={styles.hint}>
          Panelinizde güncel stand planınızı ve tüm etkinlik bilgilerini görebilirsiniz.
        </p>
      </div>
    </div>
  );
}

/* ── Küçük yardımcı bileşen ── */
function Row({ label, children }) {
  return (
    <tr>
      <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 13, width: 130 }}>
        {label}
      </td>
      <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1e3a5f", fontSize: 14 }}>
        {children}
      </td>
    </tr>
  );
}

/* ── Inline styles ── */
const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: "Arial, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 8px 40px rgba(0,0,0,0.10)",
    padding: "48px 40px",
    maxWidth: 480,
    width: "100%",
    textAlign: "center",
  },
  spinner: {
    width: 48,
    height: 48,
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #1d4ed8",
    borderRadius: "50%",
    animation: "magic-spin 0.9s linear infinite",
    margin: "0 auto 20px",
  },
  loadingText: {
    color: "#6b7280",
    fontSize: 15,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    margin: "12px 0 8px",
  },
  subtitle: {
    color: "#6b7280",
    fontSize: 14,
    lineHeight: 1.6,
    margin: "0 0 20px",
  },
  iconCircle: (bg, color) => ({
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: bg,
    color,
    fontSize: 28,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  }),
  infoBox: (bg, border) => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 12,
    padding: "16px 12px",
    marginBottom: 20,
    textAlign: "left",
  }),
  btnPrimary: {
    display: "inline-block",
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    padding: "14px 36px",
    borderRadius: 50,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: 12,
    width: "100%",
  },
  btnSecondary: {
    display: "inline-block",
    background: "#f1f5f9",
    color: "#374151",
    border: "none",
    padding: "12px 32px",
    borderRadius: 50,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
    width: "100%",
  },
  hint: {
    color: "#9ca3af",
    fontSize: 12,
    margin: "0 0 4px",
  },
};
